import {
  Alert,
  AlertIcon,
  FormControl,
  FormHelperText,
  FormLabel,
  Spinner,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { format, parseISO } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ContractDurationType,
  type ContractDurationDto,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { DATE_FIELD_FORMAT } from "../../../../../common/input/DateTimeSelectors/ThemedDatePicker";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { visibleLength } from "../../../../../common/text/visibleLength";
import { announcedExtensions, baseContractMonths, lengthWithExtensions } from "../utils/extensionLength";
import { frameworkDurationLimitExceeded, frameworkLimitWords, runsOver } from "../utils/frameworkDuration";
import { FieldSaveError } from "./FieldSaveError";

const REASON_PATH = "publicContext.frameworkOverFourYearsJustification";

export const FrameworkOverFourYearsPointer = ({ eventId }: { eventId: string }) => {
  const { t } = useTranslation();
  const { state } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const limit = frameworkDurationLimitExceeded(state);
  if (!limit) return null;
  const field = { field: t("Framework agreement"), ...frameworkLimitWords(t, limit) };
  const duration = state.eventData.contractDuration;
  return (
    <Text fontSize="sm" color="smStatusIssueText" mt="2" role="status">
      {duration?.type === ContractDurationType.OpenEnded
        ? t(
            "The framework agreement has no defined end, so the special circumstances that justify running over {{period}} are asked for under «{{field}}» below ({{basis}}).",
            field,
          )
        : runsOver(limit.years, duration)
          ? t(
              "The framework agreement runs over {{period}}, so the special circumstances behind it are asked for under «{{field}}» below ({{basis}}).",
              field,
            )
          : t(
              "With its extensions, the framework agreement runs over {{period}}, so the special circumstances behind it are asked for under «{{field}}» below ({{basis}}).",
              field,
            )}
    </Text>
  );
};

const useFrameworkDurationText = (duration: ContractDurationDto | null | undefined): string | null => {
  const { t } = useTranslation();
  if (duration?.type === ContractDurationType.PeriodBased && duration.periodMonths != null) {
    return t("The framework agreement runs {{count}} months.", { count: duration.periodMonths });
  }
  if (duration?.type === ContractDurationType.FixedDates && duration.startDate && duration.endDate) {
    return t("The framework agreement runs {{from}} – {{to}}.", {
      from: asInTheDateField(duration.startDate),
      to: asInTheDateField(duration.endDate),
    });
  }
  if (duration?.type === ContractDurationType.OpenEnded) {
    return t("The framework agreement has no defined end.");
  }
  return null;
};

const asInTheDateField = (date: string): string => format(parseISO(date), DATE_FIELD_FORMAT);

const useExtensionsText = (event: SourcingEventDto): string | null => {
  const { t } = useTranslation();
  const duration = event.eventData.contractDuration;
  if (duration?.type === ContractDurationType.OpenEnded) return null;
  const baseMonths = baseContractMonths(duration);
  const length = baseMonths == null ? null : lengthWithExtensions(baseMonths, announcedExtensions(event));
  if (!length) return null;
  return t("With all extensions, it runs up to {{total}} {{unit}}: {{parts}} {{unit}}.", {
    total: length.total,
    parts: length.parts.join(" + "),
    unit: length.unit === "year" ? t("year", { count: length.total }) : t("month", { count: length.total }),
  });
};

export const FrameworkOverFourYearsField = ({
  eventId,
}: {
  eventId: string;
}) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus } = useEditor<
    SourcingEventDto,
    SourcingEventPatchDto
  >(eventId);
  const saved =
    state.eventContext.type === "Public"
      ? state.eventContext.frameworkOverFourYearsJustification
      : null;
  const [reason, setReason] = useState(saved ?? "");
  const isSaving =
    fieldStatus(REASON_PATH) === "saving" ||
    fieldStatus(REASON_PATH) === "pending";

  const durationText = useFrameworkDurationText(state.eventData.contractDuration);
  const extensionsText = useExtensionsText(state);

  const limit = frameworkDurationLimitExceeded(state);
  if (!limit) return null;
  const words = frameworkLimitWords(t, limit);

  return (
    <FormControl mt="4" isRequired>
      <Alert
        status="warning"
        rounded="md"
        fontSize="sm"
        mb="3"
        alignItems="flex-start"
      >
        <AlertIcon />
        {durationText} {extensionsText && <>{extensionsText} </>}
        {words.rule}
      </Alert>
      <FormLabel htmlFor="framework-over-four-years" fontSize="sm">
        {t("Special circumstances that justify a framework agreement over {{period}}", words)}{" "}
        {isSaving && <Spinner size="xs" />}
      </FormLabel>
      <Textarea
        id="framework-over-four-years"
        rows={3}
        value={reason}
        onChange={(event) => {
          setReason(event.target.value);
          updateField(
            {
              publicContext: {
                frameworkOverFourYearsJustification:
                  visibleLength(event.target.value) > 0
                    ? event.target.value
                    : null,
              },
            },
            { debounceMs: 300 },
          );
        }}
      />
      <FieldSaveError eventId={eventId} path={REASON_PATH} />
      <FormHelperText>
        {t(
          "Required before publishing. Carried into the procurement protocol, so the choice can be reviewed afterwards.",
        )}
      </FormHelperText>
    </FormControl>
  );
};
